import React, { useState, useEffect } from 'react';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import Header from './components/Header';
import MainLayout from './components/MainLayout';
import SeatingArrangement from './components/seating/SeatingArrangement';
import Footer from './components/Footer';
import HelpModal from './components/HelpModal';
import BatchCourseModal from './components/BatchCourseModal';
import CourseDetailsModal from './components/CourseDetailsModal';
import TimePickerModal from './components/TimePickerModal';
import BackgroundSettingsModal from './components/BackgroundSettingsModal';
import AddTimeModal from './components/AddTimeModal';
import AddDayModal from './components/AddDayModal';
import AddCourseModal from './components/AddCourseModal';
import ConfirmModal from './components/ConfirmModal';
import TimeManagementModal from './components/TimeManagementModal';
import BingBackground from './components/BingBackground';
import GoogleAnalytics from './components/GoogleAnalytics';
import MobileDetection from './components/MobileDetection';
import DownloadButton from './components/DownloadButton';

function App() {
  const [courses, setCourses] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [weekdays, setWeekdays] = useState(['周一', '周二', '周三', '周四', '周五']);
  const [timeSlots, setTimeSlots] = useState([
    '08:00-08:45', '08:55-09:40', '10:00-10:45', '10:55-11:40',
    '14:00-14:45', '14:55-15:40', '16:00-16:45', '16:55-17:40'
  ]);
  const [title, setTitle] = useState('我的课程表');
  const [backgroundImage, setBackgroundImage] = useState('');
  const [backgroundOpacity, setBackgroundOpacity] = useState(0.3);
  const [backgroundBlur, setBackgroundBlur] = useState(0);
  const [backgroundMode, setBackgroundMode] = useState('image'); // 'image' 或 'color'
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showCourseDetailsModal, setShowCourseDetailsModal] = useState(false);
  const [showTimePickerModal, setShowTimePickerModal] = useState(false);
  const [showBackgroundSettingsModal, setShowBackgroundSettingsModal] = useState(false);
  const [showAddTimeModal, setShowAddTimeModal] = useState(false);
  const [showAddDayModal, setShowAddDayModal] = useState(false);
  const [showAddCourseModal, setShowAddCourseModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showTimeManagementModal, setShowTimeManagementModal] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const [editingCourse, setEditingCourse] = useState(null);
  const [showPeriodColumn, setShowPeriodColumn] = useState(false);
  const [currentView, setCurrentView] = useState('schedule'); // 'schedule' 或 'seating'

  // 初始化默认课程和课程表结构
  useEffect(() => {
    const defaultCourses = [
      { name: '语文', color: '#FF5252', teacher: '', location: '' },
      { name: '数学', color: '#4CAF50', teacher: '', location: '' },
      { name: '英语', color: '#2196F3', teacher: '', location: '' }
    ];
    setCourses(defaultCourses);
    
    // 初始化课程表结构
    const initialSchedule = timeSlots.map(() => new Array(weekdays.length).fill(''));
    setSchedule(initialSchedule);
  }, []);

  // 从本地存储加载数据
  useEffect(() => {
    const savedData = localStorage.getItem('scheduleData');
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        if (data.courses) setCourses(data.courses);
        if (data.schedule) setSchedule(data.schedule);
        if (data.weekdays) setWeekdays(data.weekdays);
        if (data.timeSlots) setTimeSlots(data.timeSlots);
        if (data.title) setTitle(data.title);
        if (data.backgroundImage) setBackgroundImage(data.backgroundImage);
        if (data.backgroundOpacity !== undefined) setBackgroundOpacity(data.backgroundOpacity);
        if (data.backgroundBlur !== undefined) setBackgroundBlur(data.backgroundBlur);
        if (data.showPeriodColumn !== undefined) setShowPeriodColumn(data.showPeriodColumn);
      } catch (error) {
        console.error('加载本地数据失败:', error);
      }
    }
  }, []);

  // 保存数据到本地存储
  const saveToLocalStorage = () => {
    const data = {
      courses,
      schedule,
      weekdays,
      timeSlots,
      title,
      backgroundImage,
      backgroundOpacity,
      backgroundBlur,
      backgroundMode,
      backgroundColor,
      showPeriodColumn
    };
    localStorage.setItem('scheduleData', JSON.stringify(data));
  };

  // 添加课程
  const addCourse = () => {
    setShowAddCourseModal(true);
  };

  const handleAddCourse = (courseData) => {
    if (!courses.find(c => c.name === courseData.name)) {
      const newCourse = {
        name: courseData.name,
        color: courseData.color,
        teacher: '',
        location: ''
      };
      setCourses([...courses, newCourse]);
    }
  };

  // 从课程表添加新课程（简化版本，只有课程名称）
  const addNewCourse = (courseName) => {
    if (!courses.find(c => c.name === courseName)) {
      const newCourse = {
        name: courseName,
        color: '#3B82F6', // 默认蓝色
        teacher: '',
        location: ''
      };
      setCourses([...courses, newCourse]);
    }
  };

  // 删除课程（通过右键删除）
  const removeCourse = (courseName) => {
    setConfirmAction(() => () => {
      setCourses(courses.filter(c => c.name !== courseName));
      // 同时从课程表中移除该课程
      setSchedule(schedule.map(row => 
        row.map(cell => cell === courseName ? '' : cell)
      ));
    });
    setShowConfirmModal(true);
  };

  // 更新课程信息
  const updateCourse = (updates) => {
    // 如果是单个更新（向后兼容）
    if (typeof updates === 'string' || (updates.oldName && updates.newCourse)) {
      const { oldName, newCourse } = updates;
      const updatedCourses = courses.map(c => c.name === oldName ? newCourse : c);
      setCourses(updatedCourses);
      
      // 更新课程表中的课程名称
      const updatedSchedule = schedule.map(row => 
        row.map(cell => cell === oldName ? newCourse.name : cell)
      );
      setSchedule(updatedSchedule);
      return;
    }
    
    // 批量更新
    if (Array.isArray(updates)) {
      let updatedCourses = [...courses];
      let updatedSchedule = [...schedule];
      
      updates.forEach(({ oldName, newCourse }) => {
        // 更新课程列表
        updatedCourses = updatedCourses.map(c => c.name === oldName ? newCourse : c);
        
        // 更新课程表中的课程名称
        updatedSchedule = updatedSchedule.map(row => 
          row.map(cell => cell === oldName ? newCourse.name : cell)
        );
      });
      
      setCourses(updatedCourses);
      setSchedule(updatedSchedule);
    }
  };

  // 批量添加课程
  const batchAddCourses = (courseNames) => {
    const newCourses = courseNames
      .filter(name => name.trim() && !courses.find(c => c.name === name.trim()))
      .map(name => ({
        name: name.trim(),
        color: '#' + Math.floor(Math.random() * 16777215).toString(16),
        teacher: '',
        location: ''
      }));
    setCourses([...courses, ...newCourses]);
  };

  // 批量删除课程
  const batchDeleteCourses = (courseNames) => {
    setCourses(courses.filter(c => !courseNames.includes(c.name)));
    // 从课程表中移除这些课程
    setSchedule(schedule.map(row => 
      row.map(cell => courseNames.includes(cell) ? '' : cell)
    ));
  };

  // 添加时间
  const addTime = () => {
    setShowAddTimeModal(true);
  };

  const showTimeManagement = () => {
    setShowTimeManagementModal(true);
  };

  const updateTimeSlots = (newTimeSlots) => {
    setTimeSlots(newTimeSlots);
    // 调整课程表结构以匹配新的时间数量
    const currentSchedule = [...schedule];
    const newSchedule = [];
    
    for (let i = 0; i < newTimeSlots.length; i++) {
      if (i < currentSchedule.length) {
        newSchedule.push(currentSchedule[i]);
      } else {
        newSchedule.push(new Array(weekdays.length).fill(''));
      }
    }
    
    setSchedule(newSchedule);
  };

  // 确认添加时间
  const confirmAddTime = (time) => {
    setTimeSlots([...timeSlots, time]);
    // 为新时间添加空行
    setSchedule([...schedule, new Array(weekdays.length).fill('')]);
    setShowAddTimeModal(false);
  };

  const deleteTime = (timeIndex) => {
    setConfirmAction(() => () => {
      const newTimeSlots = timeSlots.filter((_, index) => index !== timeIndex);
      const newSchedule = schedule.filter((_, index) => index !== timeIndex);
      setTimeSlots(newTimeSlots);
      setSchedule(newSchedule);
    });
    setShowConfirmModal(true);
  };

  const deleteDay = (dayIndex) => {
    setConfirmAction(() => () => {
      const newWeekdays = weekdays.filter((_, index) => index !== dayIndex);
      setWeekdays(newWeekdays);
      // 从每个时间行中删除对应的列
      setSchedule(schedule.map(row => row.filter((_, index) => index !== dayIndex)));
    });
    setShowConfirmModal(true);
  };

  // 更新时间
  const updateTime = (timeIndex, newTime) => {
    const newTimeSlots = [...timeSlots];
    newTimeSlots[timeIndex] = newTime;
    setTimeSlots(newTimeSlots);
  };

  // 添加星期
  const addDay = () => {
    setShowAddDayModal(true);
  };

  const handleAddDay = (dayName) => {
    if (dayName && !weekdays.includes(dayName)) {
      setWeekdays([...weekdays, dayName]);
      // 为每个时间行添加新的空列
      setSchedule(schedule.map(row => [...row, '']));
    }
  };

  // 处理拖拽放置
  const handleDrop = (e, timeIndex, dayIndex) => {
    e.preventDefault();
    const courseName = e.dataTransfer.getData('text/plain');
    const newSchedule = [...schedule];
    
    // 确保行存在
    if (!newSchedule[timeIndex]) {
      newSchedule[timeIndex] = new Array(weekdays.length).fill('');
    }
    
    // 确保列存在
    if (newSchedule[timeIndex].length <= dayIndex) {
      newSchedule[timeIndex] = [...newSchedule[timeIndex], ...new Array(dayIndex - newSchedule[timeIndex].length + 1).fill('')];
    }
    
    newSchedule[timeIndex][dayIndex] = courseName;
    setSchedule(newSchedule);
  };

  // 处理手动输入
  const handleManualInput = (e, timeIndex, dayIndex) => {
    // 如果e是字符串，说明是直接传递的值（如课程编辑）
    const value = typeof e === 'string' ? e : e.target.textContent.trim();
    const newSchedule = [...schedule];
    
    // 确保行存在
    if (!newSchedule[timeIndex]) {
      newSchedule[timeIndex] = new Array(weekdays.length).fill('');
    }
    
    // 确保列存在
    if (newSchedule[timeIndex].length <= dayIndex) {
      newSchedule[timeIndex] = [...newSchedule[timeIndex], ...new Array(dayIndex - newSchedule[timeIndex].length + 1).fill('')];
    }
    
    newSchedule[timeIndex][dayIndex] = value;
    setSchedule(newSchedule);
  };

  // 处理拖动更新（直接传递值）
  const handleDragUpdate = (timeIndex, dayIndex, value) => {
    const newSchedule = [...schedule];
    
    // 确保行存在
    if (!newSchedule[timeIndex]) {
      newSchedule[timeIndex] = new Array(weekdays.length).fill('');
    }
    
    // 确保列存在
    if (newSchedule[timeIndex].length <= dayIndex) {
      newSchedule[timeIndex] = [...newSchedule[timeIndex], ...new Array(dayIndex - newSchedule[timeIndex].length + 1).fill('')];
    }
    
    newSchedule[timeIndex][dayIndex] = value;
    setSchedule(newSchedule);
  };

  // 保存课程表
  const saveSchedule = () => {
    saveToLocalStorage();
    alert('课程表已保存到本地存储');
  };

  // 加载课程表
  const loadSchedule = () => {
    const savedData = localStorage.getItem('scheduleData');
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        if (data.courses) setCourses(data.courses);
        if (data.schedule) setSchedule(data.schedule);
        if (data.weekdays) setWeekdays(data.weekdays);
        if (data.timeSlots) setTimeSlots(data.timeSlots);
        if (data.title) setTitle(data.title);
        if (data.backgroundImage) setBackgroundImage(data.backgroundImage);
        if (data.backgroundOpacity !== undefined) setBackgroundOpacity(data.backgroundOpacity);
        if (data.backgroundBlur !== undefined) setBackgroundBlur(data.backgroundBlur);
        if (data.backgroundMode) setBackgroundMode(data.backgroundMode);
        if (data.backgroundColor) setBackgroundColor(data.backgroundColor);
        if (data.showPeriodColumn !== undefined) setShowPeriodColumn(data.showPeriodColumn);
        alert('课程表已从本地存储加载');
      } catch (error) {
        alert('加载失败，请检查数据格式');
      }
    } else {
      alert('没有找到保存的课程表');
    }
  };

  // 导出为图片
  const exportToImage = () => {
    import('html2canvas').then(html2canvas => {
      const exportElement = document.getElementById('exportContainer');
      if (!exportElement) {
        alert('找不到导出内容，请刷新页面后重试');
        return;
      }
      
      // 等待样式完全加载
      setTimeout(() => {
        html2canvas.default(exportElement, {
          backgroundColor: '#ffffff',
          scale: 2, // 提高图片质量
          useCORS: true,
          allowTaint: true,
          logging: false,
          width: exportElement.offsetWidth,
          height: exportElement.offsetHeight,
          scrollX: 0,
          scrollY: 0,
          windowWidth: exportElement.offsetWidth,
          windowHeight: exportElement.offsetHeight
        }).then(canvas => {
          const link = document.createElement('a');
          link.download = `${title || '课程表'}.png`;
          link.href = canvas.toDataURL('image/png', 1.0);
          link.click();
        }).catch(error => {
          console.error('导出图片失败:', error);
          alert('导出图片失败，请重试');
        });
      }, 100);
    });
  };

  // 打印课程表
  const printSchedule = () => {
    console.log('🚨 打印函数被调用了！');
    console.log('🖨️ 开始打印流程...');
    console.log('🔍 当前页面信息:', {
      userAgent: navigator.userAgent,
      url: window.location.href,
      title: document.title
    });
    
    const exportElement = document.getElementById('exportContainer');
    console.log('📋 查找导出容器:', exportElement);
    if (exportElement) {
      console.log('📋 导出容器信息:', {
        tagName: exportElement.tagName,
        className: exportElement.className,
        innerHTML: exportElement.innerHTML.substring(0, 200) + '...'
      });
    }
    
    if (!exportElement) {
      console.error('❌ 找不到导出容器元素');
      alert('找不到打印内容，请刷新页面后重试');
      return;
    }

    console.log('✅ 找到导出容器，开始创建打印窗口...');

    try {
      // 创建打印窗口
      console.log('🪟 尝试打开新窗口...');
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      console.log('🪟 打印窗口对象:', printWindow);
      
      if (!printWindow) {
        console.error('❌ 无法创建打印窗口，可能被浏览器阻止');
        alert('无法打开打印窗口，请检查浏览器弹窗设置');
        return;
      }
      
      console.log('✅ 打印窗口创建成功');
      const printContent = exportElement.cloneNode(true);
      console.log('📄 克隆打印内容:', printContent);
      
      // 复制所有内联样式到打印内容
      const copyStyles = (source, target) => {
        const computedStyle = window.getComputedStyle(source);
        for (let i = 0; i < computedStyle.length; i++) {
          const property = computedStyle[i];
          const value = computedStyle.getPropertyValue(property);
          if (value && value !== 'initial' && value !== 'inherit') {
            target.style.setProperty(property, value);
          }
        }
      };
      
      // 递归复制所有元素的样式
      const copyAllStyles = (sourceElement, targetElement) => {
        copyStyles(sourceElement, targetElement);
        const sourceChildren = sourceElement.children;
        const targetChildren = targetElement.children;
        
        for (let i = 0; i < sourceChildren.length && i < targetChildren.length; i++) {
          copyAllStyles(sourceChildren[i], targetChildren[i]);
        }
      };
      
      copyAllStyles(exportElement, printContent);
    
    // 设置打印样式 - 使用原始样式
    console.log('📝 开始写入打印样式和内容...');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title || '课程表'}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
              background: white;
              padding: 20px;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
              margin: 0;
            }
            
            .print-container {
              max-width: 100%;
              width: fit-content;
              background: white;
            }
            
            /* 打印优化 */
            @media print {
              body {
                padding: 0;
                margin: 0;
                background: white;
              }
              
              .print-container {
                max-width: none;
                width: 100%;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            ${printContent.outerHTML}
          </div>
        </body>
      </html>
    `);
    
      console.log('✅ 打印内容写入完成');
      printWindow.document.close();
      console.log('📄 文档已关闭');
      
      // 等待内容加载完成后打印
      console.log('⏳ 设置 onload 事件监听器...');
      printWindow.onload = () => {
        console.log('🎯 onload 事件触发，开始打印...');
        try {
          console.log('🎯 聚焦打印窗口...');
          printWindow.focus();
          console.log('🖨️ 调用 print() 方法...');
          printWindow.print();
          console.log('✅ print() 方法调用成功');
          // 延迟关闭窗口，确保打印对话框已打开
          setTimeout(() => {
            console.log('⏰ 延迟关闭打印窗口...');
            printWindow.close();
            console.log('✅ 打印窗口已关闭');
          }, 1000);
        } catch (error) {
          console.error('❌ onload 打印失败:', error);
          alert('打印失败，请重试');
        }
      };
      
      // 如果onload没有触发，使用setTimeout作为备选方案
      console.log('⏳ 设置备选打印方案 (500ms 后)...');
      setTimeout(() => {
        console.log('🔄 备选方案执行，检查窗口状态...');
        if (printWindow && !printWindow.closed) {
          console.log('✅ 窗口仍然打开，执行备选打印...');
          try {
            console.log('🎯 备选方案：聚焦打印窗口...');
            printWindow.focus();
            console.log('🖨️ 备选方案：调用 print() 方法...');
            printWindow.print();
            console.log('✅ 备选方案：print() 方法调用成功');
            setTimeout(() => {
              console.log('⏰ 备选方案：延迟关闭打印窗口...');
              printWindow.close();
              console.log('✅ 备选方案：打印窗口已关闭');
            }, 1000);
          } catch (error) {
            console.error('❌ 备选方案打印失败:', error);
            alert('打印失败，请重试');
          }
        } else {
          console.log('⚠️ 备选方案：窗口已关闭，跳过打印');
        }
      }, 500);
      
    } catch (error) {
      console.error('❌ 创建打印窗口失败:', error);
      console.error('❌ 错误详情:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      alert('创建打印窗口失败，请重试');
    }
  };

  // 导入课程表
  const importSchedule = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          
          // 导入所有数据
          if (data.courses) setCourses(data.courses);
          if (data.schedule) setSchedule(data.schedule);
          if (data.weekdays) setWeekdays(data.weekdays);
          if (data.timeSlots) setTimeSlots(data.timeSlots);
          if (data.title) setTitle(data.title);
          
          // 导入背景设置
          if (data.backgroundImage) setBackgroundImage(data.backgroundImage);
          if (data.backgroundOpacity !== undefined) setBackgroundOpacity(data.backgroundOpacity);
          if (data.backgroundBlur !== undefined) setBackgroundBlur(data.backgroundBlur);
          if (data.backgroundMode) setBackgroundMode(data.backgroundMode);
          if (data.backgroundColor) setBackgroundColor(data.backgroundColor);
          if (data.showPeriodColumn !== undefined) setShowPeriodColumn(data.showPeriodColumn);
          
          // 自动保存到本地存储
          setTimeout(() => {
            saveToLocalStorage();
          }, 100);
          
          alert('课程表导入成功！所有设置和配置已恢复。');
        } catch (error) {
          console.error('导入失败:', error);
          alert('文件格式错误，请选择正确的JSON文件');
        }
      };
      reader.readAsText(file);
    }
  };

  // 导出课程表
  const exportSchedule = () => {
    const data = {
      // 课程表数据
      courses,
      schedule,
      weekdays,
      timeSlots,
      title,
      
      // 背景设置
      backgroundImage,
      backgroundOpacity,
      backgroundBlur,
      backgroundMode,
      backgroundColor,
      showPeriodColumn,
      
      // 导出信息
      exportDate: new Date().toISOString(),
      version: '1.0'
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `课程表_${new Date().toLocaleDateString().replace(/\//g, '-')}.json`;
    link.click();
    URL.revokeObjectURL(url);
    
    alert('课程表导出成功！包含所有课程、设置和配置。');
  };

  // 清除本地数据
  const clearLocalData = () => {
    setConfirmAction(() => () => {
      // 清除本地存储
      localStorage.removeItem('scheduleData');
      
      // 重置所有状态到初始值
      setTitle('课程表');
      setCourses([]);
      setWeekdays(['周一', '周二', '周三', '周四', '周五']);
      setTimeSlots(['08:00-08:45', '09:00-09:45', '10:00-10:45', '11:00-11:45', '14:00-14:45', '15:00-15:45', '16:00-16:45']);
      setBackgroundImage('');
      setBackgroundOpacity(0.5);
      setBackgroundBlur(0);
      
      // 重新初始化课程表结构
      const initialSchedule = ['08:00-08:45', '09:00-09:45', '10:00-10:45', '11:00-11:45', '14:00-14:45', '15:00-15:45', '16:00-16:45'].map(() => 
        new Array(['周一', '周二', '周三', '周四', '周五'].length).fill('')
      );
      setSchedule(initialSchedule);
    });
    setShowConfirmModal(true);
  };

  return (
    <ConfigProvider locale={zhCN}>
      <GoogleAnalytics />
      <BingBackground />
      <MobileDetection>
      <div className="min-h-screen">
      <Header 
        currentView={currentView}
        onViewChange={setCurrentView}
      />
      
      {currentView === 'schedule' && (
        <MainLayout
        courses={courses}
        schedule={schedule}
        weekdays={weekdays}
        timeSlots={timeSlots}
        backgroundImage={backgroundImage}
        backgroundOpacity={backgroundOpacity}
        backgroundBlur={backgroundBlur}
        backgroundMode={backgroundMode}
        backgroundColor={backgroundColor}
        showPeriodColumn={showPeriodColumn}
        onBackgroundImageChange={setBackgroundImage}
        onBackgroundOpacityChange={setBackgroundOpacity}
        onBackgroundBlurChange={setBackgroundBlur}
        onShowBackgroundSettings={() => setShowBackgroundSettingsModal(true)}
        onTogglePeriodColumn={setShowPeriodColumn}
        onAddCourse={addCourse}
        onRemoveCourse={removeCourse}
        onShowCourseDetails={() => setShowCourseDetailsModal(true)}
        onShowBatchModal={() => setShowBatchModal(true)}
        onAddTime={addTime}
        onAddDay={addDay}
        onDeleteTime={deleteTime}
        onDeleteDay={deleteDay}
        onUpdateTime={updateTime}
        onShowTimeManagement={showTimeManagement}
        onDrop={handleDrop}
        onManualInput={handleManualInput}
        onDragUpdate={handleDragUpdate}
        onAddNewCourse={addNewCourse}
        onSave={saveSchedule}
        onLoad={loadSchedule}
        onExportImage={exportToImage}
        onPrint={printSchedule}
        onImport={importSchedule}
        onExport={exportSchedule}
        onClearData={clearLocalData}
        showHelpModal={showHelpModal}
        setShowHelpModal={setShowHelpModal}
      />
      )}
      
      {currentView === 'seating' && (
        <SeatingArrangement />
      )}
      
      <Footer onShowHelp={() => setShowHelpModal(true)} />
      
      {/* 软件下载按钮 */}
      <DownloadButton />
      
      <HelpModal 
        isOpen={showHelpModal} 
        onClose={() => setShowHelpModal(false)} 
      />
      
      <BatchCourseModal
        isOpen={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        courses={courses}
        onBatchAdd={batchAddCourses}
        onBatchDelete={batchDeleteCourses}
        onEditCourse={(course) => {
          setEditingCourse(course);
          setShowCourseDetailsModal(true);
        }}
      />
      
      <CourseDetailsModal
        isOpen={showCourseDetailsModal}
        onClose={() => {
          setShowCourseDetailsModal(false);
          setEditingCourse(null);
        }}
        courses={courses}
        editingCourse={editingCourse}
        onUpdateCourse={updateCourse}
      />
      
      <TimePickerModal
        isOpen={showTimePickerModal}
        onClose={() => setShowTimePickerModal(false)}
        onConfirm={confirmAddTime}
      />
      
      <BackgroundSettingsModal
        isOpen={showBackgroundSettingsModal}
        onClose={() => setShowBackgroundSettingsModal(false)}
        backgroundImage={backgroundImage}
        backgroundOpacity={backgroundOpacity}
        backgroundBlur={backgroundBlur}
        backgroundMode={backgroundMode}
        backgroundColor={backgroundColor}
        onBackgroundImageChange={setBackgroundImage}
        onBackgroundOpacityChange={setBackgroundOpacity}
        onBackgroundBlurChange={setBackgroundBlur}
        onBackgroundModeChange={setBackgroundMode}
        onBackgroundColorChange={setBackgroundColor}
        onClearBackground={() => setBackgroundImage('')}
        onSave={() => {
          // 自动保存到本地存储
          saveToLocalStorage();
        }}
      />

      <AddTimeModal
        isOpen={showAddTimeModal}
        onClose={() => setShowAddTimeModal(false)}
        onConfirm={confirmAddTime}
      />

      <AddDayModal
        isOpen={showAddDayModal}
        onClose={() => setShowAddDayModal(false)}
        onConfirm={handleAddDay}
      />

      <AddCourseModal
        isOpen={showAddCourseModal}
        onClose={() => setShowAddCourseModal(false)}
        onConfirm={handleAddCourse}
      />

      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => {
          if (confirmAction) {
            confirmAction();
          }
          setShowConfirmModal(false);
          setConfirmAction(null);
        }}
        title="确认清除"
        message="确定要清除所有本地数据吗？此操作将删除所有课程、课程表、背景设置等数据，且不可撤销。"
        confirmText="清除"
        cancelText="取消"
        type="danger"
      />

      <TimeManagementModal
        isOpen={showTimeManagementModal}
        onClose={() => setShowTimeManagementModal(false)}
        timeSlots={timeSlots}
        onUpdateTimeSlots={updateTimeSlots}
      />
      </div>
      </MobileDetection>
    </ConfigProvider>
  );
}

export default App;
